import { Add, Edit, OpenInNew } from '@mui/icons-material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetLegalRequirementDependencyByTransaction } from '../../../hooks/reactQuerys/LegalDependencyQueries';
import { useLegalDependency } from '../../LegalDependencyContext';
import type {
  ILegalRequirementDependencyAction,
  ILegalDependencyLookupLegalTransaction,
  ILegalRequirementDependencyCondition,
  ILegalRequirementDependencyConditionGroup,
  ILegalRequirementDependencyRule,
} from '../../../Interfaces/ILegalDependency';

interface IProps {
  idLegalRequirement: number;
  currentLegalTransactionId?: number;
  onEditRule: (ruleId: number) => void;
  onOpenDependencyEditor?: () => void;
}

interface IUsageEntry {
  key: string;
  ruleId: number | null;
  ruleName: string | null;
  ruleTransactionId: number | null;
  transactionId: number | null;
  whenCount: number;
  thenCount: number;
  executedAt: string | null;
}

const getRuleTransactionId = (rule: ILegalRequirementDependencyRule) =>
  rule.LegalTransaction_idLegalTransaction ?? rule.idLegalTransaction ?? null;

const getConditionGroupRuleId = (
  group: ILegalRequirementDependencyConditionGroup
) => group.LRDRule_idLRDRule ?? group.idLRDRule ?? null;

const getConditionRequirementId = (
  condition: ILegalRequirementDependencyCondition
) =>
  condition.LegalRequirement_idLegalRequirement ??
  condition.idLegalRequirement ??
  null;

const getConditionTransactionId = (
  condition: ILegalRequirementDependencyCondition
) =>
  condition.LegalTransaction_idLegalTransaction ??
  condition.idLegalTransaction ??
  null;

const getActionRequirementId = (action: ILegalRequirementDependencyAction) =>
  action.LegalRequirement_idLegalRequirement ??
  action.idLegalRequirement ??
  null;

const getActionRuleId = (action: ILegalRequirementDependencyAction) =>
  action.LRDRule_idLRDRule ?? action.idLRDRule ?? null;

const getActionTransactionId = (action: ILegalRequirementDependencyAction) =>
  action.LegalTransaction_idLegalTransaction ??
  action.idLegalTransaction ??
  null;

const getTransactionLabel = (
  transactionId: number | null,
  title?: string | null
) => {
  if (transactionId === null) {
    return 'Unbekannter Vorgang';
  }

  if (title && title.trim().length > 0) {
    return title;
  }

  return `Vorgang #${transactionId}`;
};

export const LegalRequirementDependencyUsage = ({
  idLegalRequirement,
  currentLegalTransactionId,
  onEditRule,
  onOpenDependencyEditor,
}: IProps) => {
  const navigate = useNavigate();
  const { legalDependencyData } = useLegalDependency();
  const {
    legalRequirementDependencyByTransaction,
    isLoadingLegalRequirementDependencyByTransaction,
    isFetchingLegalRequirementDependencyByTransaction,
  } = useGetLegalRequirementDependencyByTransaction(
    currentLegalTransactionId,
    idLegalRequirement,
    true
  );

  // Persistierte Daten aus der DB (enthalten u. a. die vorgangsübergreifende
  // Verwendung aus *anderen* Vorgängen).
  const dbRuleArray = legalRequirementDependencyByTransaction?.RuleArray ?? [];
  const dbConditionGroupArray =
    legalRequirementDependencyByTransaction?.ConditionGroupArray ?? [];
  const dbConditionArray =
    legalRequirementDependencyByTransaction?.ConditionArray ?? [];
  const dbActionArray =
    legalRequirementDependencyByTransaction?.ActionArray ?? [];
  const dbLegalTransactionLookup =
    legalRequirementDependencyByTransaction?.LegalTransactionLookup ?? [];

  // Noch nicht gespeicherte Änderungen des aktuellen Vorgangs liegen nur im
  // Frontend-State (LegalDependencyContext). Damit eine gerade angelegte,
  // geänderte oder gelöschte Regel sofort hier auftaucht, ohne erst über die
  // DB zurückzukommen, ist für den aktuellen Vorgang der Frontend-State
  // autoritativ; für andere Vorgänge bleibt die DB maßgeblich.
  const memRuleArray = legalDependencyData?.DependencyRuleArray ?? [];
  const memConditionGroupArray =
    legalDependencyData?.DependencyConditionGroupArray ?? [];
  const memConditionArray = legalDependencyData?.DependencyConditionArray ?? [];
  const memActionArray = legalDependencyData?.DependencyActionArray ?? [];
  const memLegalTransactionLookup =
    legalDependencyData?.DependencyLegalTransactionLookup ?? [];

  const {
    dependencyRuleArray,
    dependencyConditionGroupArray,
    dependencyConditionArray,
    dependencyActionArray,
    dependencyLegalTransactionLookup,
  } = useMemo(() => {
    // Ohne aktuellen Vorgang gibt es keinen relevanten Frontend-State.
    if (currentLegalTransactionId === undefined) {
      return {
        dependencyRuleArray: dbRuleArray,
        dependencyConditionGroupArray: dbConditionGroupArray,
        dependencyConditionArray: dbConditionArray,
        dependencyActionArray: dbActionArray,
        dependencyLegalTransactionLookup: dbLegalTransactionLookup,
      };
    }

    // Regel-IDs des aktuellen Vorgangs (aus DB + Frontend-State zusammen).
    const currentTxRuleIds = new Set<number>();
    [...dbRuleArray, ...memRuleArray].forEach(rule => {
      if (getRuleTransactionId(rule) === currentLegalTransactionId) {
        currentTxRuleIds.add(rule.idLRDRule);
      }
    });

    const isCurrentTxRule = (ruleId: number | null | undefined) =>
      ruleId !== null && ruleId !== undefined && currentTxRuleIds.has(ruleId);

    // Gruppe -> Regel über DB + Frontend-State, um DB-Bedingungen des
    // aktuellen Vorgangs zuverlässig herausfiltern zu können.
    const groupToRuleId = new Map<number, number | null>();
    [...dbConditionGroupArray, ...memConditionGroupArray].forEach(group => {
      groupToRuleId.set(
        group.idLRDConditionGroup,
        getConditionGroupRuleId(group)
      );
    });

    const mergedRuleArray = [
      ...dbRuleArray.filter(
        rule => getRuleTransactionId(rule) !== currentLegalTransactionId
      ),
      ...memRuleArray,
    ];

    const mergedConditionGroupArray = [
      ...dbConditionGroupArray.filter(
        group => !isCurrentTxRule(getConditionGroupRuleId(group))
      ),
      ...memConditionGroupArray,
    ];

    const mergedConditionArray = [
      ...dbConditionArray.filter(condition => {
        const groupId =
          condition.LRDConditionGroup_idLRDConditionGroup ??
          condition.idLRDConditionGroup ??
          null;
        const ruleId =
          groupId !== null ? (groupToRuleId.get(groupId) ?? null) : null;
        return !isCurrentTxRule(ruleId);
      }),
      ...memConditionArray,
    ];

    const mergedActionArray = [
      ...dbActionArray.filter(
        action => !isCurrentTxRule(getActionRuleId(action))
      ),
      ...memActionArray,
    ];

    // Vorgangs-Titel: DB + Frontend-State, eindeutig nach idLegalTransaction.
    const lookupMap = new Map<number, ILegalDependencyLookupLegalTransaction>();
    [...dbLegalTransactionLookup, ...memLegalTransactionLookup].forEach(
      entry => {
        lookupMap.set(entry.idLegalTransaction, entry);
      }
    );

    return {
      dependencyRuleArray: mergedRuleArray,
      dependencyConditionGroupArray: mergedConditionGroupArray,
      dependencyConditionArray: mergedConditionArray,
      dependencyActionArray: mergedActionArray,
      dependencyLegalTransactionLookup: Array.from(lookupMap.values()),
    };
  }, [
    currentLegalTransactionId,
    dbRuleArray,
    dbConditionGroupArray,
    dbConditionArray,
    dbActionArray,
    dbLegalTransactionLookup,
    memRuleArray,
    memConditionGroupArray,
    memConditionArray,
    memActionArray,
    memLegalTransactionLookup,
  ]);

  const isLoading =
    isLoadingLegalRequirementDependencyByTransaction ||
    isFetchingLegalRequirementDependencyByTransaction;

  const usages = useMemo<IUsageEntry[]>(() => {
    if (idLegalRequirement < 1) {
      return [];
    }

    const ruleMap = new Map<number, ILegalRequirementDependencyRule>();
    const groupRuleMap = new Map<number, number | null>();
    dependencyRuleArray.forEach(rule => {
      ruleMap.set(rule.idLRDRule, rule);
    });

    dependencyConditionGroupArray.forEach(group => {
      groupRuleMap.set(
        group.idLRDConditionGroup,
        getConditionGroupRuleId(group)
      );
    });

    const usageMap = new Map<string, IUsageEntry>();

    const addUsage = (
      ruleId: number | null,
      ruleName: string | null,
      ruleTransactionId: number | null,
      transactionId: number | null,
      executedAt: string | null,
      role: 'when' | 'then'
    ) => {
      const key = `${ruleId ?? 'unknown'}-${transactionId ?? 'unknown'}`;
      const currentUsage = usageMap.get(key) || {
        key,
        ruleId,
        ruleName,
        ruleTransactionId,
        transactionId,
        whenCount: 0,
        thenCount: 0,
        executedAt,
      };

      if (role === 'when') {
        currentUsage.whenCount += 1;
      } else {
        currentUsage.thenCount += 1;
      }

      usageMap.set(key, currentUsage);
    };

    dependencyConditionArray.forEach(condition => {
      if (condition.TargetType !== 'requirement') {
        return;
      }

      if (getConditionRequirementId(condition) !== idLegalRequirement) {
        return;
      }

      const groupId =
        condition.LRDConditionGroup_idLRDConditionGroup ??
        condition.idLRDConditionGroup ??
        null;
      const ruleId =
        groupId !== null ? (groupRuleMap.get(groupId) ?? null) : null;
      const rule = ruleId !== null ? ruleMap.get(ruleId) : undefined;
      const ruleName = rule?.Name?.trim() || null;
      const ruleTransactionId = rule ? getRuleTransactionId(rule) : null;
      const executedAt = rule?.Executed_at ?? null;
      const transactionId =
        getConditionTransactionId(condition) ?? ruleTransactionId;

      addUsage(
        ruleId,
        ruleName,
        ruleTransactionId,
        transactionId,
        executedAt,
        'when'
      );
    });

    dependencyActionArray.forEach(action => {
      if (action.TargetType !== 'requirement') {
        return;
      }

      if (getActionRequirementId(action) !== idLegalRequirement) {
        return;
      }

      const ruleId = getActionRuleId(action);
      const rule = ruleId !== null ? ruleMap.get(ruleId) : undefined;
      const ruleName = rule?.Name?.trim() || null;
      const ruleTransactionId = rule ? getRuleTransactionId(rule) : null;
      const executedAt = rule?.Executed_at ?? null;
      const transactionId = getActionTransactionId(action) ?? ruleTransactionId;

      addUsage(
        ruleId,
        ruleName,
        ruleTransactionId,
        transactionId,
        executedAt,
        'then'
      );
    });

    return Array.from(usageMap.values()).sort((a, b) => {
      const transactionA = a.transactionId ?? Number.MAX_SAFE_INTEGER;
      const transactionB = b.transactionId ?? Number.MAX_SAFE_INTEGER;

      if (transactionA !== transactionB) {
        return transactionA - transactionB;
      }

      return (
        (a.ruleId ?? Number.MAX_SAFE_INTEGER) -
        (b.ruleId ?? Number.MAX_SAFE_INTEGER)
      );
    });
  }, [
    dependencyActionArray,
    dependencyConditionArray,
    dependencyConditionGroupArray,
    dependencyRuleArray,
    idLegalRequirement,
  ]);

  const whenUsageCount = useMemo(
    () => usages.reduce((sum, usage) => sum + usage.whenCount, 0),
    [usages]
  );
  const thenUsageCount = useMemo(
    () => usages.reduce((sum, usage) => sum + usage.thenCount, 0),
    [usages]
  );

  const transactionTitleMap = useMemo(
    () =>
      new Map(
        dependencyLegalTransactionLookup.map(
          (transaction: ILegalDependencyLookupLegalTransaction) => [
            transaction.idLegalTransaction,
            transaction.Title ?? null,
          ]
        )
      ),
    [dependencyLegalTransactionLookup]
  );

  return (
    <Card variant='outlined'>
      <CardContent>
        <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1.5 }}>
          <AccountTreeIcon color='primary' />
          <Typography variant='subtitle1' fontWeight={600}>
            Verwendung in Abhängigkeiten
          </Typography>
        </Stack>

        {idLegalRequirement < 1 ? (
          <Stack
            alignItems='center'
            direction='row'
            justifyContent='space-between'
            spacing={1}
          >
            <Typography color='text.secondary' variant='body2'>
              Die Übersicht ist verfügbar, sobald die Maßnahme gespeichert
              wurde.
            </Typography>
            {onOpenDependencyEditor ? (
              <Tooltip title='Abhängigkeiten öffnen'>
                <IconButton
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    onOpenDependencyEditor();
                  }}
                  size='small'
                >
                  <Add fontSize='small' />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        ) : isLoading ? (
          <Stack
            alignItems='center'
            direction='row'
            justifyContent='space-between'
            spacing={1}
          >
            <Stack alignItems='center' direction='row' spacing={1.25}>
              <CircularProgress size={18} />
              <Typography color='text.secondary' variant='body2'>
                Abhängigkeiten werden geladen...
              </Typography>
            </Stack>
            {onOpenDependencyEditor ? (
              <Tooltip title='Abhängigkeiten öffnen'>
                <IconButton
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    onOpenDependencyEditor();
                  }}
                  size='small'
                >
                  <Add fontSize='small' />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        ) : usages.length === 0 ? (
          <Stack
            alignItems='center'
            direction='row'
            justifyContent='space-between'
            spacing={1}
          >
            <Typography color='text.secondary' variant='body2'>
              Diese Maßnahme wird in keiner Abhängigkeitsregel verwendet.
            </Typography>
            {onOpenDependencyEditor ? (
              <Tooltip title='Neue Abhängigkeit hinzufügen'>
                <IconButton
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    onOpenDependencyEditor();
                  }}
                  size='small'
                >
                  <Add fontSize='small' />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {onOpenDependencyEditor ? (
              <Stack
                alignItems='center'
                direction='row'
                justifyContent='space-between'
                spacing={1}
              >
                <Typography color='text.secondary' variant='body2'>
                  Diese Maßnahme wird in {usages.length} Abhängigkeitsregel
                  {usages.length === 1 ? '' : 'n'} verwendet.
                </Typography>
                <Tooltip title='Abhängigkeiten öffnen'>
                  <IconButton
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      onOpenDependencyEditor();
                    }}
                    size='small'
                  >
                    <Add fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Stack>
            ) : null}
            <Stack direction='row' flexWrap='wrap' spacing={1} useFlexGap>
              <Chip
                color='default'
                label={`${usages.length} Regel${usages.length === 1 ? '' : 'n'}`}
                size='small'
                variant='outlined'
              />
              <Chip
                color='info'
                label={`${whenUsageCount}x WENN`}
                size='small'
                variant='outlined'
              />
              <Chip
                color='success'
                label={`${thenUsageCount}x DANN`}
                size='small'
                variant='outlined'
              />
            </Stack>

            {usages.map(usage => (
              <Box
                key={usage.key}
                sx={{
                  border: theme => `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: 1.5,
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent='space-between'
                  spacing={1.5}
                >
                  <Box>
                    <Stack
                      direction='row'
                      alignItems='center'
                      spacing={0.75}
                      flexWrap='wrap'
                      useFlexGap
                    >
                      <Typography variant='subtitle2'>
                        {usage.ruleName
                          ? usage.ruleName
                          : usage.ruleId !== null
                            ? `Regel #${usage.ruleId}`
                            : 'Regel ohne Zuordnung'}
                      </Typography>
                      <Chip
                        label={usage.executedAt ? 'Ausgeführt' : 'Offen'}
                        color={usage.executedAt ? 'default' : 'primary'}
                        size='small'
                        variant='outlined'
                      />
                    </Stack>
                    <Typography color='text.secondary' variant='body2'>
                      {getTransactionLabel(
                        usage.transactionId,
                        usage.transactionId !== null
                          ? transactionTitleMap.get(usage.transactionId)
                          : null
                      )}
                    </Typography>
                  </Box>

                  <Stack
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                  >
                    <Stack
                      direction='row'
                      flexWrap='wrap'
                      spacing={1}
                      useFlexGap
                    >
                      {usage.whenCount > 0 ? (
                        <Chip
                          color='info'
                          label={
                            usage.whenCount === 1
                              ? 'WENN'
                              : `WENN (${usage.whenCount})`
                          }
                          size='small'
                        />
                      ) : null}
                      {usage.thenCount > 0 ? (
                        <Chip
                          color='success'
                          label={
                            usage.thenCount === 1
                              ? 'DANN'
                              : `DANN (${usage.thenCount})`
                          }
                          size='small'
                        />
                      ) : null}
                      {usage.ruleTransactionId === currentLegalTransactionId ? (
                        <Chip
                          label='Aktueller Vorgang'
                          size='small'
                          variant='outlined'
                        />
                      ) : null}
                    </Stack>

                    <Stack direction='row' spacing={0.5}>
                      {usage.ruleTransactionId !== null ? (
                        <Tooltip title='Zum Vorgang'>
                          <IconButton
                            onClick={event => {
                              event.preventDefault();
                              event.stopPropagation();
                              navigate(
                                `/legaltransaction/${usage.ruleTransactionId}`
                              );
                            }}
                            size='small'
                          >
                            <OpenInNew fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {usage.ruleId !== null ? (
                        <Tooltip title='Regel bearbeiten'>
                          <IconButton
                            onClick={event => {
                              event.preventDefault();
                              event.stopPropagation();
                              const ruleId = usage.ruleId;

                              if (ruleId === null) {
                                return;
                              }

                              if (
                                usage.ruleTransactionId !== null &&
                                usage.ruleTransactionId !==
                                  currentLegalTransactionId
                              ) {
                                navigate(
                                  `/legaltransaction/${usage.ruleTransactionId}`,
                                  {
                                    state: {
                                      openLegalDependencyEditor: true,
                                      selectedDependencyRuleId: ruleId,
                                    },
                                  }
                                );
                                return;
                              }

                              onEditRule(ruleId);
                            }}
                            size='small'
                          >
                            <Edit fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Stack>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
